import { useCallback, useState } from "react"
import * as yup from "yup"
import YupPassword from "yup-password"
import type { FormikHelpers } from "formik"

import { useStableNavigate } from "~/ui-rtk/utils/StableNavigateContext"

import {
  isErrorWithMessage,
  isFetchBaseQueryError,
} from "~/ui-rtk/utils/guards"
import { onboardingRoutes } from "~/ui-rtk/constants/routes"
import { useAuthControllerCreateAccountMutation } from "~/ui-rtk/api/authApi"

YupPassword(yup)

export const validationSchema = yup.object().shape({
  workEmail: yup
    .string()
    .email("Oops! That doesn’t look like a valid email")
    .required("Email is required!"),
  password: yup
    .string()
    .required("Password is required!")
    .min(8, "Must be at least 8 characters long")
    .minLowercase(1, "Must contain a lowercase letter (a-z)")
    .minUppercase(1, "Must contain an uppercase letter (A-Z)")
    .minNumbers(1, "Must contain a number")
    .minSymbols(1, "Must contain a symbol (e.g. @, $, !, %, *, ?)"),
  fullName: yup.string().required("Full name is required!"),
})

type FormValues = {
  fullName: string
  workEmail: string
  password: string
}

export default function useConnect() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [createAccountAsync] = useAuthControllerCreateAccountMutation()

  const navigate = useStableNavigate()

  const initialValues: FormValues = {
    workEmail: "",
    password: "",
    fullName: "",
  }

  const handleRegister = useCallback(
    async (values: FormValues, { setErrors }: FormikHelpers<FormValues>) => {
      const { workEmail, ...createAccountDto } = values
      void setIsSubmitting(true)
      try {
        await createAccountAsync({
          createAccountDto: {
            workEmail: workEmail?.toLowerCase(),
            ...createAccountDto,
            // Every self-serve signup is the SoS funnel (APP-488): the server
            // stamps the user's first company SOS_FREE off this source.
            signupSource: "share_of_search" as const,
          },
        }).unwrap()

        navigate(onboardingRoutes.CHECK_INBOX)
      } catch (error) {
        if (isFetchBaseQueryError(error) && error.status === 401) {
          setErrors({
            workEmail: "Invalid credentials",
            password: "Invalid credentials",
          })
        } else if (isFetchBaseQueryError(error)) {
          const message = isErrorWithMessage(error.data)
            ? error.data.message
            : "Something went wrong, please try again."
          setErrors({ workEmail: message })
        }
      } finally {
        void setIsSubmitting(false)
      }
    },
    [navigate],
  )

  return {
    handleRegister,
    initialValues,
    validationSchema,
    isSubmitting,
  }
}
